import { useEffect, useRef, useState } from "react";
import type { DeadReason } from "@/lib/wsReconnect";
import { subscribeTracking } from "@/features/tracking/lib/trackingSocket";
import type {
  RouteStatusUpdate,
  TrackingLocationUpdate,
} from "@/types/tracking";

interface Options {
  /** Called for every `location_update` frame. Optional — omit on pages that only need route status. */
  onLocationUpdate?: (update: TrackingLocationUpdate) => void;
  /** Called for every `route_status_update` frame. Optional. */
  onRouteStatusUpdate?: (update: RouteStatusUpdate) => void;
  enabled?: boolean;
}

/**
 * What the hook reports back to the page so a dead socket is VISIBLE.
 *
 * A silent stop is the bug, not the fix (BUG-580): on prod the page simply
 * stopped updating and the user was told nothing. `deadReason` is non-null only
 * once the hook has given up for good.
 */
export interface TrackingWebSocketState {
  /** True once the hook has stopped reconnecting permanently. */
  isDead: boolean;
  /** Why it stopped, or null while it is still trying. */
  deadReason: DeadReason | null;
}

/**
 * Subscribes to the tenant tracking WebSocket and dispatches incoming frames
 * to the matching callback based on `data.type`:
 * - `location_update`     → `onLocationUpdate`
 * - `route_status_update` → `onRouteStatusUpdate`
 *
 * Both callbacks are optional; a consumer subscribes to only the frames it
 * needs. **The connection is SHARED per tab** (`trackingSocket.ts`, BUG-738):
 * the app-wide route-status listener in the dispatcher shell and a page's GPS
 * listener (/tracking, the routes map) ride ONE socket — the first subscriber
 * opens it, the last one to unmount closes it. Several hook calls never mean
 * several sockets, so no frame is received twice.
 *
 * Reconnect policy lives in `@/lib/wsReconnect` and is shared with the
 * notifications and chat hooks (TASK-4987): jittered exponential backoff
 * 1 s → 30 s, a stop on 4001/4003, and exactly ONE token refresh on 4401 before
 * a terminal dead state, which every subscriber sees as `isDead` (BUG-580).
 */
export default function useTrackingWebSocket({
  onLocationUpdate,
  onRouteStatusUpdate,
  enabled = true,
}: Options): TrackingWebSocketState {
  const [deadReason, setDeadReason] = useState<DeadReason | null>(null);

  // Keep stable refs to the callbacks so the subscription doesn't churn when
  // they change.
  const onLocationUpdateRef = useRef(onLocationUpdate);
  onLocationUpdateRef.current = onLocationUpdate;
  const onRouteStatusUpdateRef = useRef(onRouteStatusUpdate);
  onRouteStatusUpdateRef.current = onRouteStatusUpdate;

  useEffect(() => {
    if (!enabled) return;
    setDeadReason(null);
    return subscribeTracking({
      onLocationUpdate: (u) => onLocationUpdateRef.current?.(u),
      onRouteStatusUpdate: (u) => onRouteStatusUpdateRef.current?.(u),
      onDeadChange: setDeadReason,
    });
  }, [enabled]);

  return { isDead: deadReason !== null, deadReason };
}
